import React, { useId } from 'react';
import clsx from 'clsx';
import { CircleAlert } from 'lucide-react-0.367';

import styles from './styles/Input.module.scss';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  errors?: string[];
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ 'aria-describedby': describedBy, 'aria-invalid': ariaInvalid, className, errors, type, ...props }, ref) => {
    const hasErrors = errors !== undefined && errors.length > 0;
    const id = useId();
    const errorId = `${id}-error`;

    // The messages were rendered and styled but not announced: no `aria-invalid`, and nothing tying
    // them to the input, so a screen reader reached the field with no indication it had failed.
    // `aria-describedby` is MERGED rather than set, because a caller's help-text id is just as
    // load-bearing as the error id, and an explicit `aria-invalid` from the caller still wins.
    const describedByIds = [describedBy, hasErrors ? errorId : undefined].filter(Boolean).join(' ') || undefined;

    return (
      <div className={styles.InputWrapper} data-cxy-name="input">
        <input
          aria-describedby={describedByIds}
          aria-invalid={ariaInvalid ?? (hasErrors || undefined)}
          className={clsx(styles.Input, hasErrors ? styles.error : '', className)}
          ref={ref}
          type={type}
          {...props}
        />
        {hasErrors && (
          <>
            {/* Decorative: the messages below carry the text, so the icon must not be announced. */}
            <CircleAlert aria-hidden className={styles.ErrorIcon} />
            <div id={errorId}>
              {errors.map(error => (
                <p className={styles.ErrorMessage} key={error}>
                  {error}
                </p>
              ))}
            </div>
          </>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export { Input };
